/**
 * GovBrPagination.jsx
 */

import React, { useId, useState } from 'react';

const DEFAULT_PAGE_SIZES = [10, 20, 30, 'all'];

/**
 * Ativa ação via teclado (Enter / Space)
 */
const handleKeyActivate = (event, callback) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    callback();
  }
};

const getVisiblePages = (currentPage, totalPages) => {
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = start + maxVisible - 1;

  if (end > totalPages) {
    end = totalPages;
    start = Math.max(1, end - maxVisible + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const GovBrPagination = ({
  total = 0,
  currentPage = 1,
  pageSize = 1,
  pageSizes = DEFAULT_PAGE_SIZES,
  onPageChange,
  onPageSizeChange,
}) => {
  const tranlatedPageSize = pageSize === 'all' ? total : pageSize;
  const totalPages = Math.max(1, Math.ceil(total / tranlatedPageSize));

  const perPageId = useId();
  const goToId = useId();

  const [perPageOpen, setPerPageOpen] = useState(false);
  const [goToOpen, setGoToOpen] = useState(false);

  /* 🔒 Desabilitar dropdowns quando não há escolha */
  const isPerPageDisabled = pageSizes.length <= 1;
  const isGoToDisabled = totalPages <= 1;

  const visiblePages = getVisiblePages(currentPage, totalPages);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const safePageSize = pageSize ?? pageSizes[0] ?? 10;
  const safeCurrentPage = currentPage ?? 1;

  const goPrev = () => {
    if (canGoPrev) onPageChange(currentPage - 1);
  };

  const goNext = () => {
    if (canGoNext) onPageChange(currentPage + 1);
  };

  return (
    <nav
      className="br-pagination mt-3 mb-4"
      aria-label="paginação"
      data-total={total}
      data-current={currentPage}
      data-per-page={pageSize}
    >
      {/* ================= EXIBIR ================= */}
      {!isPerPageDisabled && (
        <>
          <div className="pagination-per-page">
            <div className="br-select">
              <div className="br-input">
                <label htmlFor={`per-page-selection-${perPageId}`}>
                  Exibir
                </label>
                <input
                  id={`per-page-selection-${perPageId}`}
                  type="text"
                  value={safePageSize === 'all' ? 'Todos' : safePageSize}
                  readOnly
                  tabIndex={-1}
                  disabled={isPerPageDisabled}
                />

                <button
                  className="br-button"
                  type="button"
                  aria-label="Exibir lista"
                  aria-expanded={perPageOpen}
                  disabled={isPerPageDisabled}
                  onClick={() => {
                    if (!isPerPageDisabled) {
                      setPerPageOpen((v) => !v);
                    }
                  }}
                  onKeyDown={(e) =>
                    handleKeyActivate(e, () => {
                      if (!isPerPageDisabled) {
                        setPerPageOpen((v) => !v);
                      }
                    })
                  }
                >
                  <i className="fas fa-angle-down" aria-hidden="true" />
                </button>
              </div>
              <div
                className="br-list"
                id={`list-per-page-${perPageId}`}
                tabIndex="-1"
                role="menu"
                aria-label="Lista de Opções"
                expanded={!isPerPageDisabled && perPageOpen ? '' : undefined}
              >
                {pageSizes.map((size) => (
                  <div
                    key={size}
                    className={`br-item ${pageSize === size ? 'selected' : ''}`}
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => {
                      onPageSizeChange(size);
                      setPerPageOpen(false);
                    }}
                    onKeyDown={(e) =>
                      handleKeyActivate(e, () => {
                        onPageSizeChange(size);
                        setPerPageOpen(false);
                      })
                    }
                  >
                    <div className="br-radio">
                      <input
                        id={`per-page-${size}-${perPageId}`}
                        type="radio"
                        name={`per-page-${perPageId}`}
                        checked={pageSize === size}
                        readOnly
                      />
                      <label htmlFor={`per-page-${size}-${perPageId}`}>
                        {size === 'all' ? 'Todos' : size}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <span className="br-divider d-none d-sm-block mx-3" />
        </>
      )}

      {/* ================= INFO ================= */}
      <div className="pagination-information d-none d-sm-flex">
        <span className="current">
          {(currentPage - 1) * tranlatedPageSize + 1}
        </span>
        &ndash;
        <span className="per-page">
          {Math.min(currentPage * tranlatedPageSize, total)}
        </span>
        &nbsp;de&nbsp;
        <span className="total">{total}</span>&nbsp;itens
      </div>

      <span className="br-divider d-none d-sm-block mx-3" />

      {/* ================= PÁGINA ================= */}
      <div className="pagination-go-to-page d-none d-sm-flex">
        <div className="br-select">
          <div className="br-input">
            <label htmlFor={`go-to-selection-${goToId}`}>Página</label>
            <input
              id={`go-to-selection-${goToId}`}
              type="text"
              value={safeCurrentPage}
              readOnly
              disabled={isGoToDisabled}
              tabIndex={-1}
            />

            <button
              className="br-button"
              type="button"
              aria-label="Exibir lista"
              aria-expanded={goToOpen}
              disabled={isGoToDisabled}
              onClick={() => {
                if (!isGoToDisabled) {
                  setGoToOpen((v) => !v);
                }
              }}
              onKeyDown={(e) =>
                handleKeyActivate(e, () => {
                  if (!isGoToDisabled) {
                    setGoToOpen((v) => !v);
                  }
                })
              }
            >
              <i className="fas fa-angle-down" aria-hidden="true" />
            </button>
          </div>

          <div
            className="br-list"
            id={`list-go-to-${goToId}`}
            tabIndex="-1"
            role="menu"
            aria-label="Lista de Opções"
            expanded={!isGoToDisabled && goToOpen ? '' : undefined}
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <div
                key={page}
                className={`br-item ${currentPage === page ? 'selected' : ''}`}
                role="menuitem"
                tabIndex={0}
                onClick={() => {
                  onPageChange(page);
                  setGoToOpen(false);
                }}
                onKeyDown={(e) =>
                  handleKeyActivate(e, () => {
                    onPageChange(page);
                    setGoToOpen(false);
                  })
                }
              >
                <div className="br-radio">
                  <input
                    id={`go-to-${page}-${goToId}`}
                    type="radio"
                    name={`go-to-${goToId}`}
                    checked={currentPage === page}
                    readOnly
                  />
                  <label htmlFor={`go-to-${page}-${goToId}`}>{page}</label>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ================= SETAS ================= */}
      <div className="pagination-arrows ml-auto">
        <ul>
          {/* ⬅️ Anterior */}
          <li>
            <button
              className="br-button circle"
              type="button"
              aria-label="Página anterior"
              disabled={!canGoPrev}
              onClick={goPrev}
              onKeyDown={(e) => handleKeyActivate(e, goPrev)}
            >
              <i className="fas fa-angle-left" aria-hidden="true" />
            </button>
          </li>

          {/* 🔢 Páginas */}
          {visiblePages.map((page) => (
            <li key={page}>
              <button
                type="button"
                className={`page ${currentPage === page ? 'active' : ''}`}
                aria-current={currentPage === page ? 'page' : undefined}
                aria-label={`Página ${page}`}
                onClick={() => onPageChange(page)}
                onKeyDown={(e) =>
                  handleKeyActivate(e, () => onPageChange(page))
                }
              >
                {page}
              </button>
            </li>
          ))}

          {/* ➡️ Próxima */}
          <li>
            <button
              className="br-button circle"
              type="button"
              aria-label="Próxima página"
              disabled={!canGoNext}
              onClick={goNext}
              onKeyDown={(e) => handleKeyActivate(e, goNext)}
            >
              <i className="fas fa-angle-right" aria-hidden="true" />
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default GovBrPagination;
